import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import DrugBasicInfo from "components/molecules/DrugBasicInfo";
import DrugLabelInfo from "components/molecules/DrugLabelInfo";
import { Drug, DrugProductLabel } from "common/models/types";
import Spinner from "components/atoms/Spinner";
import { crawlDrugInfo, fetchDrugLabelByItemCode } from "api/drugService";
import { createGlobalStyle } from "styled-components";
import { log, LogLevel } from "utils/LogUtil";

interface MedicationInfoProps {
  drug?: Drug;
  onEdit?: () => void;
  isLoading?: boolean;
  onLabelsLoaded?: (labels: DrugProductLabel[], itemCode: string) => void;
}

// 테이블 스타일을 위한 전역 스타일
const GlobalTableStyle = createGlobalStyle`
  .table-wrapper-outer, ._table_wrap_out {
    overflow-x: auto;
    margin: 1em 0;
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .table-wrapper-inner, ._table_wrap_in {
    min-width: 100%;
    width: 100%;
  }

  .enhanced-table {
    width: 100%;
    border-collapse: collapse !important;
    font-size: 14px;
    background-color: white;
    border: 1px solid #d1d5db;
    margin-bottom: 1em;
  }

  .enhanced-caption {
    caption-side: top;
    margin-bottom: 10px;
    font-style: italic;
    color: #4b5563;
    text-align: left;
    font-size: 12px;
    padding: 0 0 5px 0;
  }

  .enhanced-tr-header td,
  .enhanced-tr-header th,
  .enhanced-header-cell {
    background-color: transparent !important;
    color: ${COLOR_SYSTEM.get("Gray")?.[800]} !important;
    font-weight: 600 !important;
    padding: 12px !important;
    text-align: center !important;
    border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]} !important;
    font-size: 13px !important;
    line-height: 1.4 !important;
  }

  .enhanced-tr {
    border: none;
  }

  .enhanced-tr:nth-child(even) {
    background-color: #f9fafb;
  }

  .enhanced-td {
    padding: 12px !important;
    border: 1px solid #d1d5db !important;
    vertical-align: middle !important;
    text-align: center !important;
    line-height: 1.4 !important;
  }

  .enhanced-tr:hover .enhanced-td {
    background-color: #eff6ff !important;
  }

  /* 테이블 셀 내 단락 스타일 */
  .enhanced-td p, .enhanced-header-cell p {
    margin: 0 !important;
    padding: 2px 0 !important;
  }
`;

// 컨테이너 스타일
const Container = styled.div`
  padding: 24px;
  height: 100%;
  overflow-y: auto;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  position: relative;
`;

// 빈 상태 스타일
const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

// 로딩 스타일
const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 32px;
`;

// 크롤링 버튼 스타일
const CrawlButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
  gap: 16px;
`;

// 우측 상단 버튼 스타일
const RecrawlButtonContainer = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
  display: flex;
  gap: 8px;
`;

const MedicationInfo: React.FC<MedicationInfoProps> = ({
  drug,
  onEdit,
  isLoading: initialLoading = false,
  onLabelsLoaded,
}) => {
  const [productLabels, setProductLabels] = useState<DrugProductLabel[]>([]);
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [isCrawling, setIsCrawling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 약품이 변경될 때마다 productLabels 상태 초기화
  useEffect(() => {
    if (drug?.productLabels) {
      setProductLabels(drug.productLabels);
    } else {
      setProductLabels([]);
    }
    setErrorMessage(null);
  }, [drug]);

  // 디버깅을 위한 로깅
  useEffect(() => {
    if (!drug) return;
    log(LogLevel.VERBOSE, "MedicationInfo 컴포넌트 렌더링:");
    log(LogLevel.VERBOSE, "- drug 객체:", drug);
    log(LogLevel.VERBOSE, "- drug.standardCode:", drug.standardCode);
    log(LogLevel.VERBOSE, "- drug.productLabels:", drug.productLabels);
    log(LogLevel.VERBOSE, "- productLabels 존재 여부:", Boolean(drug.productLabels));
    log(LogLevel.VERBOSE, "- productLabels 배열 여부:", Array.isArray(drug.productLabels));
    log(LogLevel.VERBOSE, "- productLabels 길이:", drug.productLabels.length);
  }, [drug]);

  // productLabels가 존재하고 배열인지 확인
  const hasValidProductLabels = useMemo(
    () => productLabels && Array.isArray(productLabels) && productLabels.length > 0,
    [productLabels]
  );

  // 크롤링 실행 함수
  const handleCrawl = async () => {
    if (!drug?.itemCode) {
      setErrorMessage("약품 코드가 없어 크롤링할 수 없습니다.");
      return;
    }

    try {
      setIsCrawling(true);
      setErrorMessage(null);

      // 크롤링 API 호출
      const crawlResponse = await crawlDrugInfo(drug.itemCode);
      log(LogLevel.INFO, "크롤링 응답:", crawlResponse);

      // 크롤링 후 라벨 정보 다시 가져오기
      const labels = await fetchDrugLabelByItemCode(drug.itemCode);

      // 로컬 상태 업데이트
      setProductLabels(labels);

      // 상위 컴포넌트에게도 업데이트된 라벨 정보 전달
      if (onLabelsLoaded && labels) {
        onLabelsLoaded(labels, drug.itemCode);
      }

      log(LogLevel.INFO, "크롤링 및 라벨 로딩 완료:", labels);
    } catch (error) {
      log(LogLevel.ERROR, "크롤링 실패:", error);
      setErrorMessage("데이터를 가져오는 중 오류가 발생했습니다. 나중에 다시 시도해주세요.");
    } finally {
      setIsCrawling(false);
    }
  };

  // 허가사항 페이지로 이동하는 함수
  const goToPermissionPage = () => {
    if (!drug?.itemCode) return;
    window.open(
      `https://nedrug.mfds.go.kr/pbp/CCBBB01/getItemDetail?itemSeq=${drug.itemCode}`,
      "_blank"
    );
  };

  // 선택된 의약품이 없는 경우
  if (!drug) {
    return (
      <Container>
        <EmptyState>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            왼쪽에서 의약품을 선택하세요
          </Text>
        </EmptyState>
      </Container>
    );
  }

  // 라벨 로딩 중인 경우
  if (isLoading || isCrawling) {
    return (
      <Container>
        <DrugBasicInfo drug={drug} onEdit={onEdit} />
        <LoadingState>
          <Spinner />
        </LoadingState>
      </Container>
    );
  }

  log(LogLevel.VERBOSE, "유효한 productLabels 있음:", hasValidProductLabels);

  return (
    <Container>
      <GlobalTableStyle />
      {/* 의약품 기본 정보 */}
      <DrugBasicInfo drug={drug} onEdit={onEdit} />

      {/* 의약품 허가사항 정보 - 유효한 productLabels가 있을 때만 표시 */}
      {hasValidProductLabels ? (
        <>
          {/* 다시 크롤링하기 버튼 */}
          <RecrawlButtonContainer>
            <Button
              buttonType="button"
              type="IconWithText"
              size="Small"
              variant="Ghost"
              color="Secondary"
              icon="Refresh"
              left
              onClick={handleCrawl}
              disabled={isCrawling}
            >
              다시 크롤링하기
            </Button>
            <Button
              buttonType="button"
              type="IconWithText"
              size="Small"
              variant="Ghost"
              color="Primary"
              icon="ExternalLink"
              left
              onClick={goToPermissionPage}
            >
              허가사항 보러가기
            </Button>
          </RecrawlButtonContainer>

          <DrugLabelInfo
            labelInfo={productLabels}
            itemCode={drug.itemCode}
            standardCode={drug.standardCode}
            mainCode={drug.mainCode}
            showMetadata={true}
          />
        </>
      ) : (
        <CrawlButtonContainer>
          <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            이 의약품에 대한 허가사항 정보가 없습니다
          </Text>

          {errorMessage && (
            <Text textType="Body2" color={COLOR_SYSTEM.get("Red")?.[400]}>
              {errorMessage}
            </Text>
          )}

          <Button
            buttonType="button"
            type="IconWithText"
            size="Medium"
            variant="Contained"
            color="Primary"
            icon="Search"
            left
            onClick={handleCrawl}
          >
            크롤링하기
          </Button>
        </CrawlButtonContainer>
      )}
    </Container>
  );
};

export default MedicationInfo;
